import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Paperclip, Upload, Trash2, Download, FileText, Archive, Folder, FolderOpen, Plus, Edit2, X, Search } from "lucide-react";
import { entities, uploadFile, getDisplayMessage, downloadUploadFile } from "@/api/apiClient";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import FileViewer from "../shared/FileViewer";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function AnexosEdital({ edital, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [anexoParaExcluir, setAnexoParaExcluir] = useState(null);
  const [anexosSelecionados, setAnexosSelecionados] = useState([]);
  const [anexosSelecionadosPasta, setAnexosSelecionadosPasta] = useState({});
  const [baixando, setBaixando] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  // Estados para pastas
  const [dialogPastaAberto, setDialogPastaAberto] = useState(false);
  const [nomePasta, setNomePasta] = useState("");
  const [pastaExpandida, setPastaExpandida] = useState({});
  const [pastaParaExcluir, setPastaParaExcluir] = useState(null);
  const [pastaEditando, setPastaEditando] = useState(null);
  const [novoNomePasta, setNovoNomePasta] = useState("");
  const [anexoEditando, setAnexoEditando] = useState(null); // { tipo: 'raiz' | 'pasta', index, pastaId? }
  const [novoNomeAnexo, setNovoNomeAnexo] = useState("");
  const [buscaAnexos, setBuscaAnexos] = useState("");

  const anexos = edital.anexos_adicionais || [];
  const pastas = edital.pastas_anexos || [];

  const busca = buscaAnexos.trim().toLowerCase();
  const buscaAtiva = busca.length > 0;

  const indicesRaizFiltrados = useMemo(() => {
    if (!buscaAtiva) return anexos.map((_, i) => i);
    return anexos
      .map((a, i) => (a.nome?.toLowerCase()?.includes(busca) ? i : -1))
      .filter((i) => i >= 0);
  }, [anexos, busca, buscaAtiva]);

  const pastasComFiltro = useMemo(() => {
    return pastas
      .map((pasta) => {
        const anexosLista = pasta.anexos || [];
        const pastaNomeMatch = buscaAtiva && (pasta.nome?.toLowerCase()?.includes(busca) ?? false);
        const anexosFiltradosIndices = !buscaAtiva
          ? anexosLista.map((_, i) => i)
          : pastaNomeMatch
            ? anexosLista.map((_, i) => i)
            : anexosLista
                .map((a, i) => (a.nome?.toLowerCase()?.includes(busca) ? i : -1))
                .filter((i) => i >= 0);
        const pastaVisivel =
          !buscaAtiva ||
          (pasta.nome?.toLowerCase()?.includes(busca) ?? false) ||
          anexosLista.some((a) => a.nome?.toLowerCase()?.includes(busca));
        return { pasta, anexosFiltradosIndices, pastaVisivel };
      })
      .filter((p) => p.pastaVisivel);
  }, [pastas, busca, buscaAtiva]);

  useEffect(() => {
    if (!buscaAtiva) return;
    setPastaExpandida((prev) => {
      const next = { ...prev };
      pastas.forEach((pasta) => {
        const nomeOk = pasta.nome?.toLowerCase()?.includes(busca) ?? false;
        const fileOk = (pasta.anexos || []).some((a) => a.nome?.toLowerCase()?.includes(busca));
        if (nomeOk || fileOk) next[pasta.id] = true;
      });
      return next;
    });
  }, [pastas, busca, buscaAtiva]);

  const handleUploadAnexos = async (e, pastaId = null) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });

    try {
      const novosAnexosLista = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ current: i + 1, total: files.length });
        
        try {
          const { file_url } = await uploadFile(file);
          
          const novoAnexo = {
            url: file_url,
            nome: file.name,
            data_adicao: new Date().toISOString()
          };
          
          novosAnexosLista.push(novoAnexo);
        } catch (error) {
          console.error(`Erro ao fazer upload do arquivo ${file.name}:`, error);
        }
      }
      
      // Se for para uma pasta específica
      if (pastaId) {
        const novasPastas = pastas.map(pasta => {
          if (pasta.id === pastaId) {
            return {
              ...pasta,
              anexos: [...(pasta.anexos || []), ...novosAnexosLista]
            };
          }
          return pasta;
        });
        
        // Preserva os anexos da raiz ao adicionar anexos em uma pasta
        await entities.Edital.update(edital.id, {
          pastas_anexos: novasPastas,
          anexos_adicionais: anexos  // Preservar anexos existentes na raiz
        });
      } else {
        // Adiciona na raiz - preserva as pastas existentes
        await entities.Edital.update(edital.id, {
          anexos_adicionais: [...anexos, ...novosAnexosLista],
          pastas_anexos: pastas  // Preservar pastas existentes
        });
      }

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao fazer upload dos arquivos:", error);
      toast.error(getDisplayMessage(error, "Erro ao fazer upload dos arquivos. Alguns arquivos podem não ter sido adicionados."), { duration: 5000 });
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      e.target.value = '';
    }
  };

  const handleCriarPasta = async () => {
    if (!nomePasta.trim()) return;

    const novaPasta = {
      id: `pasta_${Date.now()}`,
      nome: nomePasta,
      data_criacao: new Date().toISOString(),
      anexos: []
    };

    const novasPastas = [...pastas, novaPasta];
    
    try {
      // Preserva os anexos existentes ao criar a pasta
      await entities.Edital.update(edital.id, {
        pastas_anexos: novasPastas,
        anexos_adicionais: anexos  // Preservar anexos existentes
      });

      setNomePasta("");
      setDialogPastaAberto(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao criar pasta:", error);
      toast.error(getDisplayMessage(error, "Erro ao criar pasta. Tente novamente."));
    }
  };

  const handleRenomearPasta = async () => {
    if (!novoNomePasta.trim() || !pastaEditando) return;

    const novasPastas = pastas.map(pasta => {
      if (pasta.id === pastaEditando) {
        return { ...pasta, nome: novoNomePasta };
      }
      return pasta;
    });

    try {
      // Preserva os anexos existentes ao renomear a pasta
      await entities.Edital.update(edital.id, {
        pastas_anexos: novasPastas,
        anexos_adicionais: anexos  // Preservar anexos existentes
      });

      setPastaEditando(null);
      setNovoNomePasta("");
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao renomear pasta:", error);
      toast.error(getDisplayMessage(error, "Erro ao renomear pasta. Tente novamente."));
    }
  };

  const handleExcluirPasta = async () => {
    if (!pastaParaExcluir) return;

    const pasta = pastas.find(p => p.id === pastaParaExcluir);
    
    // Move anexos da pasta para raiz
    const anexosDaPasta = pasta?.anexos || [];
    const novosAnexosRaiz = [...anexos, ...anexosDaPasta];
    
    const novasPastas = pastas.filter(p => p.id !== pastaParaExcluir);
    
    try {
      await entities.Edital.update(edital.id, {
        pastas_anexos: novasPastas,
        anexos_adicionais: novosAnexosRaiz
      });

      setPastaParaExcluir(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao excluir pasta:", error);
      toast.error(getDisplayMessage(error, "Erro ao excluir pasta. Tente novamente."));
    }
  };

  const handleRenomearAnexo = async () => {
    if (!anexoEditando || !novoNomeAnexo.trim()) return;

    const { tipo, pastaId, index } = anexoEditando;
    const nomeLimpo = novoNomeAnexo.trim();

    try {
      if (tipo === 'raiz') {
        const novosAnexos = anexos.map((anexo, i) =>
          i === index ? { ...anexo, nome: nomeLimpo } : anexo
        );
        await entities.Edital.update(edital.id, {
          anexos_adicionais: novosAnexos,
          pastas_anexos: pastas,
        });
      } else if (tipo === 'pasta') {
        const novasPastas = pastas.map((pasta) => {
          if (pasta.id === pastaId) {
            const novos = (pasta.anexos || []).map((anexo, i) =>
              i === index ? { ...anexo, nome: nomeLimpo } : anexo
            );
            return { ...pasta, anexos: novos };
          }
          return pasta;
        });
        await entities.Edital.update(edital.id, {
          pastas_anexos: novasPastas,
          anexos_adicionais: anexos,
        });
      }

      setAnexoEditando(null);
      setNovoNomeAnexo("");
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao renomear anexo:", error);
      toast.error(getDisplayMessage(error, "Erro ao renomear anexo. Tente novamente."));
    }
  };

  const handleExcluirAnexo = async () => {
    if (anexoParaExcluir === null) return;

    const { tipo, pastaId, index } = anexoParaExcluir;

    try {
      if (tipo === 'raiz') {
        const novosAnexos = anexos.filter((_, i) => i !== index);
        // Preserva as pastas ao excluir anexo da raiz
        await entities.Edital.update(edital.id, {
          anexos_adicionais: novosAnexos,
          pastas_anexos: pastas  // Preservar pastas existentes
        });
      } else {
        const novasPastas = pastas.map(pasta => {
          if (pasta.id === pastaId) {
            return {
              ...pasta,
              anexos: pasta.anexos.filter((_, i) => i !== index)
            };
          }
          return pasta;
        });
        
        // Preserva os anexos da raiz ao excluir anexo de uma pasta
        await entities.Edital.update(edital.id, {
          pastas_anexos: novasPastas,
          anexos_adicionais: anexos  // Preservar anexos existentes na raiz
        });
      }

      if (onUpdate) onUpdate();
      setDialogAberto(false);
      setAnexoParaExcluir(null);
    } catch (error) {
      console.error("Erro ao excluir anexo:", error);
      toast.error(getDisplayMessage(error, "Erro ao excluir anexo. Tente novamente."));
      setDialogAberto(false);
      setAnexoParaExcluir(null);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Parse do ID: "raiz-0" ou "pasta_123-0" (droppableId pode ser undefined em edge cases)
    const sourceParts = (source?.droppableId && typeof source.droppableId === 'string' ? source.droppableId : 'raiz-0').split('-');
    const destParts = (destination?.droppableId && typeof destination.droppableId === 'string' ? destination.droppableId : 'raiz-0').split('-');
    const [sourceLocation, sourceIndex] = [sourceParts[0] ?? 'raiz', sourceParts[1] ?? '0'];
    const [destLocation, destIndex] = [destParts[0] ?? 'raiz', destParts[1] ?? '0'];
    
    let anexoMovido;
    let novosAnexosRaiz = [...anexos];
    let novasPastas = JSON.parse(JSON.stringify(pastas));

    // Pegar o anexo da origem
    if (sourceLocation === 'raiz') {
      const idx = parseInt((draggableId && String(draggableId).split('-')[1]) || '0', 10);
      anexoMovido = novosAnexosRaiz[idx];
      novosAnexosRaiz.splice(idx, 1);
    } else {
      const pastaOrigem = novasPastas.find(p => p.id === sourceLocation);
      const idx = parseInt((draggableId && String(draggableId).split('-')[1]) || '0', 10);
      anexoMovido = pastaOrigem.anexos[idx];
      pastaOrigem.anexos.splice(idx, 1);
    }

    // Adicionar no destino
    if (destLocation === 'raiz') {
      novosAnexosRaiz.splice(destination.index, 0, anexoMovido);
    } else {
      const pastaDestino = novasPastas.find(p => p.id === destLocation);
      if (!pastaDestino.anexos) pastaDestino.anexos = [];
      pastaDestino.anexos.splice(destination.index, 0, anexoMovido);
    }

    try {
      await entities.Edital.update(edital.id, {
        anexos_adicionais: novosAnexosRaiz,
        pastas_anexos: novasPastas
      });

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Erro ao mover anexo:", error);
      toast.error(getDisplayMessage(error, "Erro ao mover anexo. Tente novamente."));
    }
  };

  const togglePasta = (pastaId) => {
    setPastaExpandida(prev => ({
      ...prev,
      [pastaId]: !prev[pastaId]
    }));
  };

  const handleSelecionarTodos = () => {
    const target = buscaAtiva ? indicesRaizFiltrados : anexos.map((_, i) => i);
    if (target.length === 0) return;
    const allSelected = target.every((i) => anexosSelecionados.includes(i));
    if (allSelected) {
      setAnexosSelecionados((prev) => prev.filter((i) => !target.includes(i)));
    } else {
      setAnexosSelecionados((prev) => [...new Set([...prev, ...target])]);
    }
  };

  const handleToggleSelecao = (index) => {
    setAnexosSelecionados(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleBaixarSelecionados = async () => {
    if (anexosSelecionados.length === 0) return;

    setBaixando(true);
    try {
      for (const index of anexosSelecionados) {
        const anexo = anexos[index];
        await downloadUploadFile(anexo.url, anexo.nome);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      setAnexosSelecionados([]);
    } catch (error) {
      console.error("Erro ao baixar arquivos:", error);
      toast.error(getDisplayMessage(error, "Erro ao baixar arquivos. Tente novamente."));
    } finally {
      setBaixando(false);
    }
  };

  const handleExcluirSelecionados = async () => {
    if (anexosSelecionados.length === 0) return;
    
    if (!confirm(`Tem certeza que deseja excluir ${anexosSelecionados.length} anexo(s) selecionado(s)?`)) {
      return;
    }

    try {
      const novosAnexos = anexos.filter((_, index) => !anexosSelecionados.includes(index));
      
      // Preserva as pastas ao excluir anexos selecionados da raiz
      await entities.Edital.update(edital.id, {
        anexos_adicionais: novosAnexos,
        pastas_anexos: pastas  // Preservar pastas existentes
      });

      setAnexosSelecionados([]);
      onUpdate();
    } catch (error) {
      console.error("Erro ao excluir anexos:", error);
      toast.error(getDisplayMessage(error, "Erro ao excluir anexos. Tente novamente."));
    }
  };

  const handleToggleSelecaoPasta = (pastaId, index) => {
    setAnexosSelecionadosPasta(prev => {
      const selecionados = prev[pastaId] || [];
      const novosSelecionados = selecionados.includes(index)
        ? selecionados.filter(i => i !== index)
        : [...selecionados, index];
      
      return {
        ...prev,
        [pastaId]: novosSelecionados
      };
    });
  };

  const handleSelecionarTodosPasta = (pastaId, indicesOriginais) => {
    if (!indicesOriginais || indicesOriginais.length === 0) return;
    setAnexosSelecionadosPasta((prev) => {
      const selecionados = prev[pastaId] || [];
      const allSelected = indicesOriginais.every((i) => selecionados.includes(i));
      if (allSelected) {
        return { ...prev, [pastaId]: selecionados.filter((i) => !indicesOriginais.includes(i)) };
      }
      return { ...prev, [pastaId]: [...new Set([...selecionados, ...indicesOriginais])] };
    });
  };

  const handleBaixarSelecionadosPasta = async (pastaId, anexosPasta) => {
    const selecionados = anexosSelecionadosPasta[pastaId] || [];
    if (selecionados.length === 0) return;

    setBaixando(true);
    try {
      for (const index of selecionados) {
        const anexo = anexosPasta[index];
        await downloadUploadFile(anexo.url, anexo.nome);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      setAnexosSelecionadosPasta(prev => ({ ...prev, [pastaId]: [] }));
    } catch (error) {
      console.error("Erro ao baixar arquivos:", error);
      toast.error(getDisplayMessage(error, "Erro ao baixar arquivos. Tente novamente."));
    } finally {
      setBaixando(false);
    }
  };

  const handleBaixarTodosPasta = async (anexosPasta) => {
    if (!anexosPasta || anexosPasta.length === 0) return;

    setBaixando(true);
    try {
      for (const anexo of anexosPasta) {
        await downloadUploadFile(anexo.url, anexo.nome);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error("Erro ao baixar arquivos:", error);
      toast.error(getDisplayMessage(error, "Erro ao baixar arquivos. Tente novamente."));
    } finally {
      setBaixando(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return '';
    }
  };

  const getFileIcon = (fileName) => {
    return <FileText className="w-5 h-5 text-blue-600" />;
  };

  const totalAnexos = anexos.length + pastas.reduce((sum, pasta) => sum + (pasta.anexos?.length || 0), 0);

  const resultadoBuscaCount = useMemo(() => {
    if (!buscaAtiva) return totalAnexos;
    return (
      indicesRaizFiltrados.length +
      pastasComFiltro.reduce((sum, { anexosFiltradosIndices }) => sum + anexosFiltradosIndices.length, 0)
    );
  }, [buscaAtiva, totalAnexos, indicesRaizFiltrados, pastasComFiltro]);

  return (
    <>
      <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between flex-wrap gap-3 w-full">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Paperclip className="w-5 h-5 text-indigo-600" />
              Anexos do Edital ({totalAnexos})
              {buscaAtiva && (
                <span className="text-sm font-normal text-slate-500">
                  — {resultadoBuscaCount} resultado(s)
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setDialogPastaAberto(true)}
                size="sm"
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                <Folder className="w-4 h-4 mr-2" />
                Nova Pasta
              </Button>
              {indicesRaizFiltrados.length > 0 && (
                <>
                  <Button
                    onClick={handleSelecionarTodos}
                    variant="outline"
                    size="sm"
                  >
                    {(() => {
                      const target = buscaAtiva ? indicesRaizFiltrados : anexos.map((_, i) => i);
                      const allSel = target.length > 0 && target.every((i) => anexosSelecionados.includes(i));
                      return allSel ? 'Desmarcar Todos' : 'Selecionar Todos';
                    })()}
                  </Button>
                  {anexosSelecionados.length > 0 && (
                    <>
                      <Button
                        onClick={handleBaixarSelecionados}
                        disabled={baixando}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        {baixando ? 'Baixando...' : `Baixar ${anexosSelecionados.length}`}
                      </Button>
                      <Button
                        onClick={handleExcluirSelecionados}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir {anexosSelecionados.length}
                      </Button>
                    </>
                  )}
                </>
              )}
              <div className="relative">
                <Input
                  type="file"
                  onChange={(e) => handleUploadAnexos(e)}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload-multiple"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
                  multiple
                />
                <Button
                  onClick={() => document.getElementById('file-upload-multiple')?.click()}
                  disabled={uploading}
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading 
                    ? `Enviando ${uploadProgress.current}/${uploadProgress.total}...` 
                    : 'Adicionar Anexos'}
                </Button>
              </div>
            </div>
          </div>
          <div className="relative w-full max-w-md mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              className="pl-9"
              placeholder="Buscar por nome do arquivo ou pasta..."
              value={buscaAnexos}
              onChange={(e) => setBuscaAnexos(e.target.value)}
              aria-label="Buscar anexos"
            />
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {uploading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">
                  Enviando arquivos...
                </span>
                <span className="text-sm text-blue-700">
                  {uploadProgress.current} de {uploadProgress.total}
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {totalAnexos === 0 && (
            <div className="text-center py-12">
              <Paperclip className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">Nenhum anexo adicionado</p>
              <p className="text-sm text-slate-400 mb-4">
                Adicione documentos complementares relacionados a este edital
              </p>
              <Button
                onClick={() => document.getElementById('file-upload-multiple')?.click()}
                variant="outline"
              >
                <Upload className="w-4 h-4 mr-2" />
                Adicionar Primeiro Anexo
              </Button>
            </div>
          )}

          {totalAnexos > 0 && resultadoBuscaCount === 0 && buscaAtiva && (
            <div className="text-center py-12 text-slate-500 border border-dashed border-slate-200 rounded-lg">
              <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium">Nenhum anexo ou pasta corresponde à busca.</p>
              <p className="text-xs text-slate-400 mt-1">Tente outro termo ou limpe o campo de busca.</p>
            </div>
          )}

          {totalAnexos > 0 && resultadoBuscaCount > 0 && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="space-y-4">
              {/* Pastas */}
              {pastasComFiltro.map(({ pasta, anexosFiltradosIndices }) => (
                <div key={pasta.id} className="border-2 border-green-200 rounded-lg bg-green-50/50">
                  <div className="flex items-center justify-between p-4 bg-green-100/50">
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => togglePasta(pasta.id)}
                        className="hover:bg-green-200 p-1 rounded transition-colors"
                      >
                        {pastaExpandida[pasta.id] ? (
                          <FolderOpen className="w-5 h-5 text-green-700" />
                        ) : (
                          <Folder className="w-5 h-5 text-green-700" />
                        )}
                      </button>
                      {pastaEditando === pasta.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={novoNomePasta}
                            onChange={(e) => setNovoNomePasta(e.target.value)}
                            className="h-8 bg-white"
                            autoFocus
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleRenomearPasta();
                            }}
                          />
                          <Button
                            size="sm"
                            onClick={handleRenomearPasta}
                            className="h-8"
                          >
                            OK
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setPastaEditando(null);
                              setNovoNomePasta("");
                            }}
                            className="h-8"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-1">
                          <span className="font-semibold text-green-900">{pasta.nome}</span>
                          <span className="text-sm text-green-700">
                            ({pasta.anexos?.length || 0} {pasta.anexos?.length === 1 ? 'arquivo' : 'arquivos'})
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {anexosFiltradosIndices.length > 0 && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSelecionarTodosPasta(pasta.id, anexosFiltradosIndices)}
                            className="text-xs"
                          >
                            {(() => {
                              const sel = anexosSelecionadosPasta[pasta.id] || [];
                              const allVis =
                                anexosFiltradosIndices.length > 0 &&
                                anexosFiltradosIndices.every((i) => sel.includes(i));
                              return allVis ? 'Desmarcar' : 'Selecionar';
                            })()}
                          </Button>
                          {(anexosSelecionadosPasta[pasta.id]?.length || 0) > 0 && (
                            <Button
                              size="sm"
                              onClick={() => handleBaixarSelecionadosPasta(pasta.id, pasta.anexos)}
                              disabled={baixando}
                              className="bg-blue-600 hover:bg-blue-700 text-xs"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              Baixar ({anexosSelecionadosPasta[pasta.id]?.length ?? 0})
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleBaixarTodosPasta(pasta.anexos)}
                            disabled={baixando}
                            className="bg-green-600 hover:bg-green-700 text-xs"
                          >
                            <Archive className="w-3 h-3 mr-1" />
                            Baixar Todos
                          </Button>
                        </>
                      )}
                      <Input
                        type="file"
                        onChange={(e) => handleUploadAnexos(e, pasta.id)}
                        className="hidden"
                        id={`upload-pasta-${pasta.id}`}
                        multiple
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => document.getElementById(`upload-pasta-${pasta.id}`)?.click()}
                        className="text-green-700 hover:bg-green-200"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setPastaEditando(pasta.id);
                          setNovoNomePasta(pasta.nome);
                        }}
                        className="text-blue-700 hover:bg-blue-100"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPastaParaExcluir(pasta.id)}
                        className="text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {pastaExpandida[pasta.id] && (
                    <Droppable droppableId={pasta.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`p-4 space-y-2 min-h-[100px] ${
                            snapshot.isDraggingOver ? 'bg-green-100' : ''
                          }`}
                        >
                          {anexosFiltradosIndices.length > 0 ? (
                            anexosFiltradosIndices.map((index, visualIndex) => {
                              const anexo = pasta.anexos[index];
                              return (
                              <Draggable
                                key={`${pasta.id}-${index}`}
                                draggableId={`${pasta.id}-${index}`}
                                index={visualIndex}
                                isDragDisabled={buscaAtiva}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                      (anexosSelecionadosPasta[pasta.id] || []).includes(index)
                                        ? 'border-blue-500 bg-blue-50'
                                        : snapshot.isDragging
                                        ? 'border-green-500 bg-white shadow-lg'
                                        : 'border-slate-200 bg-white hover:bg-slate-50'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <Checkbox
                                        checked={(anexosSelecionadosPasta[pasta.id] || []).includes(index)}
                                        onCheckedChange={() => handleToggleSelecaoPasta(pasta.id, index)}
                                      />
                                      {getFileIcon(anexo.nome)}
                                    <div className="flex-1 min-w-0">
                                      {anexoEditando && anexoEditando.tipo === 'pasta' && anexoEditando.pastaId === pasta.id && anexoEditando.index === index ? (
                                        <div className="flex items-center gap-2">
                                          <Input
                                            value={novoNomeAnexo}
                                            onChange={(e) => setNovoNomeAnexo(e.target.value)}
                                            className="h-8 bg-white"
                                            autoFocus
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') handleRenomearAnexo();
                                              if (e.key === 'Escape') {
                                                setAnexoEditando(null);
                                                setNovoNomeAnexo("");
                                              }
                                            }}
                                          />
                                          <Button
                                            size="sm"
                                            className="h-8"
                                            onClick={handleRenomearAnexo}
                                          >
                                            OK
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-8"
                                            onClick={() => {
                                              setAnexoEditando(null);
                                              setNovoNomeAnexo("");
                                            }}
                                          >
                                            <X className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="font-medium text-slate-900 truncate">
                                            {anexo.nome}
                                          </p>
                                          <p className="text-xs text-slate-500">
                                            {formatDate(anexo.data_adicao)}
                                          </p>
                                        </>
                                      )}
                                    </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <FileViewer
                                        fileUrl={anexo.url}
                                        fileName={anexo.nome}
                                        compact={true}
                                      />
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          setAnexoEditando({ tipo: 'pasta', pastaId: pasta.id, index });
                                          setNovoNomeAnexo(anexo.nome);
                                        }}
                                        className="hover:bg-slate-100 hover:text-slate-700"
                                        title="Renomear"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          setAnexoParaExcluir({ tipo: 'pasta', pastaId: pasta.id, index });
                                          setDialogAberto(true);
                                        }}
                                        className="hover:bg-red-100 hover:text-red-700"
                                        title="Excluir"
                                      >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                                )}
                              </Draggable>
                            );
                            })
                          ) : (
                            <div className="text-center py-8 text-slate-500">
                              <Folder className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                              <p className="text-sm">Pasta vazia</p>
                              <p className="text-xs mt-1">Arraste arquivos para cá ou clique no botão de upload</p>
                            </div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )}
                </div>
              ))}

              {/* Arquivos na Raiz */}
              {indicesRaizFiltrados.length > 0 && (
                <div className="border-2 border-slate-200 rounded-lg">
                  <div className="p-3 bg-slate-50 border-b border-slate-200">
                    <span className="font-semibold text-slate-700">
                      Arquivos na Raiz (
                      {buscaAtiva ? `${indicesRaizFiltrados.length} de ${anexos.length}` : anexos.length}
                      )
                    </span>
                  </div>
                  <Droppable droppableId="raiz">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`p-4 space-y-2 ${
                          snapshot.isDraggingOver ? 'bg-blue-50' : ''
                        }`}
                      >
                        {indicesRaizFiltrados.map((index, visualIndex) => {
                          const anexo = anexos[index];
                          return (
                          <Draggable
                            key={`raiz-${index}`}
                            draggableId={`raiz-${index}`}
                            index={visualIndex}
                            isDragDisabled={buscaAtiva}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                  anexosSelecionados.includes(index)
                                    ? 'border-blue-500 bg-blue-50'
                                    : snapshot.isDragging
                                    ? 'border-blue-500 bg-white shadow-lg'
                                    : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                                }`}
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <Checkbox
                                    checked={anexosSelecionados.includes(index)}
                                    onCheckedChange={() => handleToggleSelecao(index)}
                                  />
                                  {getFileIcon(anexo.nome)}
                                  <div className="flex-1 min-w-0">
                                    {anexoEditando && anexoEditando.tipo === 'raiz' && anexoEditando.index === index ? (
                                      <div className="flex items-center gap-2">
                                        <Input
                                          value={novoNomeAnexo}
                                          onChange={(e) => setNovoNomeAnexo(e.target.value)}
                                          className="h-8 bg-white"
                                          autoFocus
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleRenomearAnexo();
                                            if (e.key === 'Escape') {
                                              setAnexoEditando(null);
                                              setNovoNomeAnexo("");
                                            }
                                          }}
                                        />
                                        <Button
                                          size="sm"
                                          className="h-8"
                                          onClick={handleRenomearAnexo}
                                        >
                                          OK
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-8"
                                          onClick={() => {
                                            setAnexoEditando(null);
                                            setNovoNomeAnexo("");
                                          }}
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <>
                                        <p className="font-medium text-slate-900 truncate">
                                          {anexo.nome}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                          {formatDate(anexo.data_adicao)}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <FileViewer
                                    fileUrl={anexo.url}
                                    fileName={anexo.nome}
                                    compact={true}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setAnexoEditando({ tipo: 'raiz', index });
                                      setNovoNomeAnexo(anexo.nome);
                                    }}
                                    className="hover:bg-slate-100 hover:text-slate-700"
                                    title="Renomear"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setAnexoParaExcluir({ tipo: 'raiz', index });
                                      setDialogAberto(true);
                                    }}
                                    className="hover:bg-red-100 hover:text-red-700"
                                    title="Excluir"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              )}
            </div>
          </DragDropContext>
          )}
        </CardContent>
      </Card>

      {/* Dialog para Criar Pasta */}
      <Dialog open={dialogPastaAberto} onOpenChange={setDialogPastaAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Pasta</DialogTitle>
            <DialogDescription>
              Crie uma pasta para organizar seus anexos
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="nome-pasta">Nome da Pasta</Label>
            <Input
              id="nome-pasta"
              value={nomePasta}
              onChange={(e) => setNomePasta(e.target.value)}
              placeholder="Digite o nome da pasta..."
              className="mt-2"
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleCriarPasta();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogPastaAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCriarPasta} disabled={!nomePasta.trim()}>
              Criar Pasta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Excluir Pasta */}
      <AlertDialog open={!!pastaParaExcluir} onOpenChange={() => setPastaParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Pasta</AlertDialogTitle>
            <AlertDialogDescription>
              Os arquivos dentro desta pasta serão movidos para a raiz. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPastaParaExcluir(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExcluirPasta}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir Pasta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para Excluir Anexo */}
      <AlertDialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este anexo? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAnexoParaExcluir(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExcluirAnexo}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}